import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  useProvideGatingPermissions,
  type AuthorizationRuleForGatingType,
  type AuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import { useIsGated } from "@/features/gating/evaluation/use-is-gated";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { useGatingEvaluator } from "@/features/gating/evaluation/use-gating-evaluator";
import { ChannelPermission } from "@f3/shared/permissions";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import { useChannelStore } from "@/stores/channel.store";
import type { GatingStrategyType } from "@/features/gating/strategies/gating-strategy";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { OfferCardData } from "./offer-card.vue";

/** A gating requirement rendered as a labelled white pill (new-layout card). */
export interface OfferCriterionPill {
  icon: string;
  labelKey: string;
  qualified: boolean;
}

/** A gating requirement rendered as a stacked star badge (legacy card). */
export interface OfferGatingCriterionIcon {
  icon: string;
  qualified: boolean;
}

/**
 * Derived state + gating wiring shared by the offer card's two presentational
 * bodies (legacy + new-layout). The card is a thin dispatcher that owns the
 * gating provide/inject and CTA handler here — in ONE place — so both bodies
 * stay purely presentational and receive their data as props.
 */
export function useOfferCard(
  getOffer: () => OfferCardData,
  onOpenDetail: () => void,
) {
  const { t } = useI18n();
  const channelStore = useChannelStore();

  useProvideGatingPermissions(
    computed(
      () =>
        getOffer()
          .authorization_rule_group as AuthorizationRuleGroupForGatingType,
    ),
  );

  const gatingEvaluator = useGatingEvaluator();

  const isGated = useIsGated(
    () => getOffer().authorization_rule_group,
    ChannelPermission.claimOffers,
  );

  // Gating: wrap the CTA action so the auth modal shows when user doesn't qualify
  const gatingExperience = computed(() => ({
    experienceId: getOffer().id,
    experienceTitle: getOffer().name,
    experienceType: ExperienceType.Offer,
    experienceAction: ExperienceAction.CLAIM_OFFER,
  }));

  const { handler: onCtaClick } = useGatingForAction(
    ChannelPermission.claimOffers,
    onOpenDetail,
    { gatingExperience },
  );

  const isSoldOut = computed(
    () => getOffer().poolStats != null && getOffer().poolStats!.available === 0,
  );

  const hasClaimed = computed(() => getOffer().user_has_claimed);

  const ctaLabel = computed(() => {
    if (hasClaimed.value) return t("channel.offers.card.cta.claimed");
    if (isSoldOut.value) return t("channel.offers.card.cta.soldOut");
    return t("channel.offers.card.cta.claimNow");
  });

  const ctaClasses = computed(() => {
    if (hasClaimed.value) return "bg-fill-brand";
    if (isSoldOut.value) return "bg-fill-disabled";
    return "bg-fill-brand-secondary";
  });

  /**
   * Theme-adaptive label + icon colour matched to the active CTA background so
   * the text stays readable on the light theme (and remains correct on dark):
   *   - Claimed → `bg-fill-brand` (opaque brand red): white `text-on-brand`.
   *   - Sold Out → `bg-fill-disabled` (light grey on light): dark `text`.
   *   - Open / gated → `bg-fill-brand-secondary` (pale brand on light): dark
   *     `text`.
   * The label/icons inherit this via `currentColor`, replacing the old no-op
   * `text-text-primary` / `text-icon-primary` classes.
   */
  const ctaContentClass = computed(() =>
    hasClaimed.value ? "text-text-on-brand" : "text-text",
  );

  const cardAriaLabel = computed(() => {
    if (hasClaimed.value) {
      return `${getOffer().name} — Already Claimed`;
    }
    if (isSoldOut.value) {
      return `${getOffer().name} — Sold Out`;
    }
    if (isGated.value) {
      return `${getOffer().name} — Members only`;
    }
    return getOffer().name;
  });

  const tooltipLabel = computed(() => {
    const offer = getOffer();
    return offer.short_description || offer.description || offer.name;
  });

  /** Body description line shown under the title in the new-layout card. */
  const descriptionLabel = computed(() => {
    const offer = getOffer();
    return offer.short_description || offer.description || "";
  });

  /**
   * Status pill label on the image. Mirrors the CTA state so a sold-out /
   * claimed reward reads correctly, defaulting to "Open" for an available
   * reward.
   */
  const statusLabel = computed(() => {
    if (hasClaimed.value) return t("channel.offers.card.badges.claimed");
    if (isSoldOut.value) return t("channel.offers.card.badges.soldOut");
    return t("channel.offers.card.badges.open");
  });

  /**
   * White-pill CTA styling for the new layout. The design's button is a solid
   * white pill with dark text on the red card. Claimed / sold-out keep the same
   * pill but dim the affordance so the state still reads on the red panel.
   */
  const newCtaClasses = computed(() => {
    if (hasClaimed.value) return "bg-surface text-text opacity-90";
    if (isSoldOut.value) return "bg-fill-disabled text-text";
    return "bg-surface text-text hover:bg-surface-hover";
  });

  /**
   * Requirements pills for the new-layout card. Reuses the same
   * gating-criterion evaluation as the legacy star badges but surfaces a
   * translated label per criterion type (e.g. Points / Token) styled as the
   * design's white pills.
   */
  const criterionPills = computed<OfferCriterionPill[]>(() => {
    const group = getOffer()
      .authorization_rule_group as ResultOf<AuthorizationRuleGroupForGatingType>;

    const rules = (group?.authorization_rules ??
      []) as ResultOf<AuthorizationRuleForGatingType>[];
    if (!rules?.length) return [];

    const gatingTypes = rules.reduce(
      (acc, rule) => {
        const types = gatingEvaluator.getGatingTypesForRule(rule);
        const matches = gatingEvaluator.matchRule(rule);
        const gatingTypeMatches = types.reduce(
          (typeAcc, type) => ({
            ...typeAcc,
            [type]: (acc[type] ?? true) && matches,
          }),
          {} as Record<GatingStrategyType, boolean>,
        );
        return { ...acc, ...gatingTypeMatches };
      },
      {} as Record<GatingStrategyType, boolean>,
    );

    return (Object.entries(gatingTypes) as [GatingStrategyType, boolean][]).map(
      ([type, qualified]) => {
        const metadata = gatingEvaluator.getMetadataForGatingType(type);
        return { icon: metadata.icon, labelKey: metadata.labelKey, qualified };
      },
    );
  });

  const gatingCriterionIcons = computed<OfferGatingCriterionIcon[]>(() => {
    const group = getOffer()
      .authorization_rule_group as ResultOf<AuthorizationRuleGroupForGatingType>;

    const rules = (group?.authorization_rules ??
      []) as ResultOf<AuthorizationRuleForGatingType>[];
    if (!rules?.length) return [];

    const gatingTypes = rules.reduce(
      (acc, rule) => {
        const types = gatingEvaluator.getGatingTypesForRule(rule);
        const matches = gatingEvaluator.matchRule(rule);

        const gatingTypeMatches = types.reduce(
          (typeAcc, type) => {
            return { ...typeAcc, [type]: (acc[type] ?? true) && matches };
          },
          {} as Record<GatingStrategyType, boolean>,
        );

        return { ...acc, ...gatingTypeMatches };
      },
      {} as Record<GatingStrategyType, boolean>,
    );

    return (Object.entries(gatingTypes) as [GatingStrategyType, boolean][])
      .map(([type, qualified]) => {
        const metadata = gatingEvaluator.getMetadataForGatingType(type);
        return { icon: metadata.icon, qualified };
      })
      .filter((item) => !!item);
  });

  /** Map criterion icons to bundled raster assets (no iconify match). */
  const iconImageOverrides: Record<string, string> = {
    "material-symbols:token-outline": "/gating-wallet.png",
  };

  const canManageOffers = computed(() => channelStore.permissions.manageOffers);

  return {
    onCtaClick,
    isGated,
    hasClaimed,
    isSoldOut,
    ctaLabel,
    ctaClasses,
    ctaContentClass,
    newCtaClasses,
    cardAriaLabel,
    statusLabel,
    descriptionLabel,
    tooltipLabel,
    criterionPills,
    gatingCriterionIcons,
    iconImageOverrides,
    canManageOffers,
  };
}
